import { useEffect, useRef, useState, type RefObject } from 'react'

interface Props {
  containerRef: RefObject<HTMLElement | null>
  onSave: (selectedText: string) => void
}

export default function SelectionToolbar({ containerRef, onSave }: Props) {
  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const cachedTextRef = useRef('')
  const toolbarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>

    const handleSelectionChange = () => {
      clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        const sel = window.getSelection()
        if (!sel || sel.isCollapsed || !sel.toString().trim()) {
          // Don't hide immediately — user might be tapping our button
          return
        }

        const text = sel.toString().trim()
        if (!text || !containerRef.current) return

        // Check selection is within our container
        const range = sel.getRangeAt(0)
        if (!containerRef.current.contains(range.commonAncestorContainer)) {
          setVisible(false)
          return
        }

        // Show only for selections inside an AI response. The assistant message wrapper
        // carries [data-exchange-id]; testing that (instead of '.prose') means selections
        // that cross block boundaries — a setup sentence + its bullet list, or prose + a
        // Bible-verse card — still match, because their common ancestor is the prose div
        // itself (or a wrapper node) rather than a text node inside one block. User messages
        // have no [data-exchange-id], so they stay excluded.
        const node = range.commonAncestorContainer
        const el = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement
        if (!el?.closest('[data-exchange-id]')) {
          setVisible(false)
          return
        }

        cachedTextRef.current = text

        const rect = range.getBoundingClientRect()
        const containerRect = containerRef.current.getBoundingClientRect()
        const scrollTop = containerRef.current.scrollTop

        const HANDLE_CLEARANCE = 40 // drop below the native bottom drag handle so it isn't covered
        const HALF_W = 60 // approx half the "Save" pill width (now larger), for the horizontal clamp

        // Always place BELOW the selection, dropped far enough that the native bottom drag
        // handle (which sits at the selection's end) clears the pill. A tight below-placement
        // got covered by the handle; an above-placement collided with the native copy/select
        // menu — both bugs Tony hit. This drop sits the pill under the handle, clear of both.
        const top = rect.bottom - containerRect.top + scrollTop + HANDLE_CLEARANCE

        // Center on the selection, clamped so the pill (centered via -translate-x-1/2) stays
        // fully on screen instead of being pushed under the edge.
        const rawCenter = rect.left - containerRect.left + rect.width / 2
        const left = Math.max(HALF_W + 4, Math.min(containerRect.width - HALF_W - 4, rawCenter))

        setPosition({ top, left })
        setVisible(true)
      }, 300)
    }

    const handlePointerDown = (e: PointerEvent) => {
      // Don't dismiss if tapping our toolbar
      if (toolbarRef.current?.contains(e.target as Node)) return
      // Dismiss on tap elsewhere after a delay (let selection clear naturally)
      setTimeout(() => {
        const sel = window.getSelection()
        if (!sel || sel.isCollapsed || !sel.toString().trim()) {
          setVisible(false)
        }
      }, 200)
    }

    document.addEventListener('selectionchange', handleSelectionChange)
    document.addEventListener('pointerdown', handlePointerDown)

    return () => {
      clearTimeout(debounceTimer)
      document.removeEventListener('selectionchange', handleSelectionChange)
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [containerRef])

  const handleSave = () => {
    if (cachedTextRef.current) {
      onSave(cachedTextRef.current)
      setVisible(false)
      window.getSelection()?.removeAllRanges()
    }
  }

  if (!visible) return null

  return (
    <div
      ref={toolbarRef}
      className="absolute z-30 -translate-x-1/2 animate-slide-up"
      style={{ top: position.top, left: position.left }}
    >
      <button
        onMouseDown={(e) => e.preventDefault()}
        onTouchStart={(e) => e.preventDefault()}
        onClick={handleSave}
        className="flex items-center gap-2 px-6 py-3.5 rounded-full bg-surface border border-elevated shadow-lg text-lg font-medium text-yellow-400 hover:bg-elevated transition-colors"
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
        </svg>
        Save
      </button>
    </div>
  )
}
